import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import PasswordInput from "../components/PasswordInput";
import { ShieldCheck } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(name, email, password);
      nav("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-slate-200 p-8 fade-up">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-xl bg-teal-700 text-white flex items-center justify-center"><ShieldCheck className="w-5 h-5" /></div>
          <div>
            <div className="font-display text-xl font-bold text-slate-900">SafeHaven</div>
            <div className="text-xs text-slate-500">Safety planning for hospitals and communities</div>
          </div>
        </div>
        <h1 className="font-display text-2xl font-bold text-slate-900 mb-1">Create your account</h1>
        <p className="text-sm text-slate-500 mb-6">Your plan is private and stored securely.</p>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-sm text-slate-700 font-medium">Your name</label>
            <input
              data-testid="register-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="mt-1 w-full border border-slate-300 rounded-xl px-3 py-2.5 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
              placeholder="What should we call you?"
            />
          </div>
          <div>
            <label className="text-sm text-slate-700 font-medium">Email</label>
            <input
              data-testid="register-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1 w-full border border-slate-300 rounded-xl px-3 py-2.5 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label className="text-sm text-slate-700 font-medium">Password</label>
            <PasswordInput testId="register-password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} placeholder="At least 6 characters" />
          </div>
          {error && <div className="text-sm bg-red-50 text-red-700 border border-red-200 px-3 py-2 rounded-xl" data-testid="register-error">{error}</div>}
          <button
            data-testid="register-submit"
            disabled={loading}
            className="w-full bg-teal-700 hover:bg-teal-800 disabled:opacity-60 text-white rounded-xl py-3 font-semibold transition-colors"
          >
            {loading ? "Creating account…" : "Create account"}
          </button>
        </form>

        <p className="text-sm text-slate-500 mt-6 text-center">
          Already have an account? <Link data-testid="register-to-login" to="/login" className="text-teal-700 font-medium hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
